/**
 * Project Archive Modal Component
 *
 * Non-dismissible modal that appears when a user with multiple projects
 * downgrades to Free plan. User must select one project to keep active,
 * while other projects are archived.
 *
 * @module @ideoz/ui/components/molecules/ProjectArchiveModal
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../atoms/dialog';
import { Button } from '../atoms/button';
import { cn } from '@ideoz/ui';
import { AlertTriangle, Folder, ChevronRight } from 'lucide-react';
import type { ProjectArchiveModalProps, ProjectForArchive } from '../../types/ProjectArchiveModal.types';

/**
 * Format relative time string from date
 */
const formatRelativeTime = (date: string | Date | undefined): string => {
  if (!date) return '';

  const now = new Date();
  const targetDate = new Date(date);
  const diffMs = now.getTime() - targetDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffDays === 0) return 'Updated today';
  if (diffDays === 1) return 'Updated 1 day ago';
  if (diffDays < 7) return `Updated ${diffDays} days ago`;
  if (diffWeeks === 1) return 'Updated 1 week ago';
  if (diffWeeks < 4) return `Updated ${diffWeeks} weeks ago`;
  if (diffMonths === 1) return 'Updated 1 month ago';
  return `Updated ${diffMonths} months ago`;
};

/**
 * Project list item component
 */
interface ProjectItemProps {
  project: ProjectForArchive;
  isSelected: boolean;
  onSelect: () => void;
  testId?: string;
}

const ProjectItem: React.FC<ProjectItemProps> = ({
  project,
  isSelected,
  onSelect,
  testId,
}) => {
  const fileCountText = project.fileCount !== undefined ? `${project.fileCount} files` : '';
  const updatedText = formatRelativeTime(project.updatedAt);
  const metaText = [fileCountText, updatedText].filter(Boolean).join(' - ');

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left',
        'hover:bg-gray-50',
        isSelected
          ? 'border-green-500 bg-green-50/30'
          : 'border-gray-200 bg-white'
      )}
      data-testid={testId}
    >
      {/* Radio button */}
      <div
        className={cn(
          'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
          isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300'
        )}
      >
        {isSelected && (
          <div className="w-2 h-2 rounded-full bg-white" />
        )}
      </div>

      {/* Folder icon */}
      <Folder className="w-5 h-5 text-gray-400 flex-shrink-0" />

      {/* Project info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {project.name}
        </p>
        {metaText && (
          <p className="text-xs text-gray-500 truncate">
            {metaText}
          </p>
        )}
      </div>

      {/* Chevron */}
      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
    </button>
  );
};

/**
 * ProjectArchiveModal Component
 *
 * Non-dismissible modal for selecting a project to keep after downgrade.
 * User must select exactly one project. All other projects will be archived.
 *
 * @component
 * @example
 * ```tsx
 * <ProjectArchiveModal
 *   isOpen={true}
 *   projects={[
 *     { id: '1', name: 'Project A', fileCount: 24, updatedAt: '2025-01-20' },
 *     { id: '2', name: 'Project B', fileCount: 12, updatedAt: '2025-01-15' },
 *   ]}
 *   onUpgradeInstead={() => navigateToUpgrade()}
 *   onKeepProject={(id) => handleKeepProject(id)}
 *   isProcessing={false}
 * />
 * ```
 */
export const ProjectArchiveModal: React.FC<ProjectArchiveModalProps> = ({
  isOpen,
  projects,
  onUpgradeInstead,
  onKeepProject,
  isProcessing = false,
  className = '',
  testId = 'project-archive-modal',
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Calculate number of projects that will be archived
  const archiveCount = useMemo(() => {
    return Math.max(0, projects.length - 1);
  }, [projects.length]);

  // Handle project selection
  const handleSelectProject = (projectId: string) => {
    if (!isProcessing) {
      setSelectedProjectId(projectId);
    }
  };

  // Handle keep project button click
  const handleKeepProject = () => {
    if (selectedProjectId && !isProcessing) {
      onKeepProject(selectedProjectId);
    }
  };

  // Check if keep button should be disabled
  const isKeepDisabled = !selectedProjectId || isProcessing;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className={cn('sm:max-w-lg', className)}
        data-testid={testId}
        onInteractOutside={(e) => {
          // Prevent closing on backdrop click - modal is non-dismissible
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing on Escape - modal is non-dismissible
          e.preventDefault();
        }}
      >
        {/* Header - No close button (non-dismissible) */}
        <DialogHeader>
          <DialogTitle>
            Choose a project to keep
          </DialogTitle>
        </DialogHeader>

        {/* Description */}
        <DialogDescription>
          Free plan includes 1 project. Select which project you'd like to keep.
          Your other projects will be archived and can be restored by upgrading.
        </DialogDescription>

        {/* Warning Banner */}
        <div
          className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200"
          data-testid={`${testId}-warning-banner`}
        >
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">
            Your {archiveCount} unselected project{archiveCount !== 1 ? 's' : ''} will be archived.
            Archived projects can be restored anytime by upgrading to a paid plan.
          </p>
        </div>

        {/* Project Selection */}
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-700 mb-3">
            Select one project to keep
          </p>

          {/* Project List */}
          <div
            className="space-y-2 max-h-64 overflow-y-auto pr-1"
            data-testid={`${testId}-project-list`}
          >
            {projects.map((project) => (
              <ProjectItem
                key={project.id}
                project={project}
                isSelected={selectedProjectId === project.id}
                onSelect={() => handleSelectProject(project.id)}
                testId={`${testId}-project-${project.id}`}
              />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <DialogFooter className="mt-6">
          <Button
            onClick={onUpgradeInstead}
            disabled={isProcessing}
            variant="secondary-gray"
            data-testid={`${testId}-upgrade-button`}
          >
            Upgrade instead
          </Button>
          <Button
            onClick={handleKeepProject}
            disabled={isKeepDisabled}
            variant="default"
            data-testid={`${testId}-keep-button`}
          >
            {isProcessing ? 'Processing...' : 'Keep project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

ProjectArchiveModal.displayName = 'ProjectArchiveModal';

export default ProjectArchiveModal;
